import React, { useState } from 'react';
import PropTypes from 'prop-types';
import UsageMeters, { UsageBreakdown, diskTotal } from './UsageMeters';
import MySandboxStats from './MySandboxStats';
import PlanDialog, { AskPlanButton, PlanRequestStatus } from './PlanRequest';
import Disclosure from './Disclosure';
import ToolIcon from './ToolIcon';
import { MetersSkeleton, connectedToolsOf } from './SandboxSections';
import { SkeletonBar } from '../Skeletons';
import useRememberedToggle from '../../hooks/useRememberedToggle';
import { formatRelativeTime } from '../../utils/relativeTime';
import './sandbox.css';
import '../../pages/SandboxHome.css';

const METERS = ['cpu', 'memory', 'disk', 'services'];

/** The AI tools that reached the sandbox, as links, and when the latest of them was used; "Connect" when none. */
export function AiToolsLine({ sandbox = null, recent = [], loading = false, onConnect, onOpenTool }) {
  if (loading && !sandbox) {
    return <span aria-busy="true" aria-label="Loading your AI tools" className="sbx-usage-tools"><SkeletonBar height={12} width="200px" /></span>;
  }
  const connected = connectedToolsOf(sandbox, recent);
  if (connected.length === 0) {
    return (
      <span className="sbx-usage-tools">
        <button className="sbx-link sbx-inline-link" onClick={onConnect} type="button">Connect your AI tool</button>
      </span>
    );
  }
  const last = connected
    .map(({ seen }) => seen.lastSeenAt)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))[0];
  return (
    <span className="sbx-usage-tools">
      AI tools:{' '}
      {connected.map(({ item }, index) => (
        <React.Fragment key={item.key}>
          {index > 0 ? ', ' : ''}
          <button className="sbx-link sbx-inline-link sbx-tool-link" onClick={() => onOpenTool(item)} type="button">
            <ToolIcon icon={item.icon} label={item.name} size={14} />{item.name}
          </button>
        </React.Fragment>
      ))}
      {last ? ` · last used ${formatRelativeTime(last)}` : ''}
    </span>
  );
}

AiToolsLine.propTypes = {
  sandbox: PropTypes.object,
  recent: PropTypes.array,
  loading: PropTypes.bool,
  onConnect: PropTypes.func.isRequired,
  onOpenTool: PropTypes.func.isRequired,
};

/**
 * Home's one usage card (docs/SANDBOX_FIRST.md): CPU, memory, disk (code + data volumes) and services against the
 * plan, the AI tools that work on the sandbox, and behind "Details" the active time, the disk by store, the plan with
 * "Ask for another plan" and the recent events. The disclosure's choice is remembered for this viewer.
 */
export default function UsageCard({
  sandbox = null, stats = null, planRequests = null, onPlanRequests, loading = false, recent = [], onConnect, onOpenTool,
}) {
  const [open, toggle] = useRememberedToggle('flow.home.usageDetailsOpen');
  const [planDialog, setPlanDialog] = useState(false);
  return (
    <section aria-labelledby="sbx-usage" className="sbx-card sbx-home-section sbx-usage">
      <h2 className="sbx-sr-only" id="sbx-usage">Usage</h2>
      {loading && !sandbox ? <MetersSkeleton /> : <UsageMeters disk={diskTotal(stats?.disk)} only={METERS} plain sandbox={sandbox} />}
      <div className="sbx-usage-foot">
        <AiToolsLine loading={loading} onConnect={onConnect} onOpenTool={onOpenTool} recent={recent} sandbox={sandbox} />
        <span className="sbx-run-spacer" />
        {/* The meters are the plan's limits: asking for another plan is one click from them (once: with the details
            open, the plan card there has it). */}
        {!open && <AskPlanButton data={planRequests} onOpen={() => setPlanDialog(true)} />}
        <Disclosure controls="sbx-usage-more" onToggle={toggle} open={open}>
          {open ? 'Hide details' : 'Details: active time, disk, plan, events'}
        </Disclosure>
      </div>
      <div className="sbx-usage-more" hidden={!open} id="sbx-usage-more">
        {open && (
          <>
            <MySandboxStats
              onAskPlan={() => setPlanDialog(true)}
              plan={sandbox?.plan}
              planFooter={(
                <>
                  <PlanRequestStatus data={planRequests} onChange={onPlanRequests} />
                  <UsageBreakdown sandbox={sandbox} />
                </>
              )}
              planRequests={planRequests}
              stats={stats}
            />
            <p className="sbx-usage-note">Only you and your sandbox admin see these details.</p>
          </>
        )}
      </div>
      <PlanDialog
        data={planRequests}
        onClose={() => setPlanDialog(false)}
        onSent={(next) => { onPlanRequests(next); setPlanDialog(false); }}
        open={planDialog}
      />
    </section>
  );
}

UsageCard.propTypes = {
  sandbox: PropTypes.object,
  stats: PropTypes.object,
  planRequests: PropTypes.object,
  onPlanRequests: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  recent: PropTypes.array,
  onConnect: PropTypes.func.isRequired,
  onOpenTool: PropTypes.func.isRequired,
};
